import { useState, useRef, useEffect } from 'react';
import type { ActionConfig } from '../../config/actions';

interface ActionMenuProps {
  actions: ActionConfig[];
  onExecute: (action: ActionConfig) => void;
  isExecuting?: boolean;
}

export function ActionMenu({ actions, onExecute, isExecuting = false }: ActionMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  if (actions.length === 0) return null;

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 rounded-xl hover:bg-sage-100 text-sage-600"
        aria-label="More actions"
      >
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-sage-200 z-50 animate-fade-in">
          <div className="py-2">
            {actions.map((action) => (
              <button
                key={action.id}
                onClick={() => {
                  onExecute(action);
                  setIsOpen(false);
                }}
                disabled={isExecuting}
                className="w-full px-4 py-2 text-left text-sm text-sage-700 hover:bg-sage-50 flex items-center gap-2 disabled:opacity-50"
              >
                <span className="text-sage-400">{action.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
